import React, { useMemo } from "react";
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import {
  getRiskBadgeClass,
  getSeverityClasses,
  hasCc,
  hasMcc,
  isPrincipal,
} from "./severity";
import type {
  DiagnosisReadmissionDriver,
  DispositionEvidence,
  ReadmissionDetailsPanelProps,
  ToolContributions,
} from "./types";

const ROW_GRID =
  "grid grid-cols-[30px_minmax(0,1fr)_56px_78px_104px_32px] items-center gap-2";

const TOOL_LABELS: { key: keyof ToolContributions; label: string }[] = [
  { key: "eightP", label: "8P" },
  { key: "lace", label: "LACE" },
  { key: "lacePlus", label: "LACE+" },
  { key: "hospitalScore", label: "Hospital Score" },
  { key: "cci", label: "CCI" },
  { key: "mortalityRisk", label: "Mortality" },
];

const DISPOSITION_LABELS: {
  key: keyof DiagnosisReadmissionDriver["dispositionSensitivity"];
  label: string;
}[] = [
  { key: "home", label: "Home" },
  { key: "homeHealth", label: "Home Health" },
  { key: "snf", label: "SNF" },
  { key: "irf", label: "IRF" },
  { key: "ltach", label: "LTACH" },
];

const AiMark: React.FC = () => (
  <img
    src="/ai-bulwark-logo.svg"
    alt=""
    aria-hidden
    className="h-3 w-auto shrink-0 object-contain"
  />
);

type ExpandedProps = {
  driver: DiagnosisReadmissionDriver;
  onEvidenceClick?: (evidence: DispositionEvidence) => void;
};

const DriverExpandedDetails: React.FC<ExpandedProps> = ({
  driver,
  onEvidenceClick,
}) => {
  const activeTools = TOOL_LABELS.filter(
    ({ key }) => driver.toolContributions?.[key]
  );
  const hasReasons = (driver.predictedReadmissionReasons ?? []).length > 0;
  const hasMitigations = (driver.recommendedMitigations ?? []).length > 0;
  const hasDisposition = DISPOSITION_LABELS.some(({ key }) =>
    Boolean(driver.dispositionSensitivity?.[key])
  );
  const hasRationale =
    Boolean(driver.baselineRisk) ||
    Boolean(driver.clinicalActivity) ||
    activeTools.length > 0 ||
    hasReasons ||
    hasMitigations ||
    hasDisposition;

  const baselineTone = getSeverityClasses(driver.baselineRisk);
  const activityTone = getSeverityClasses(driver.clinicalActivity);

  return (
    <div className="space-y-3 border-t border-violet-200 bg-violet-50 px-3 py-3">
      {(driver.evidences ?? []).length > 0 && (
        <div className="overflow-x-auto rounded-md border border-violet-200 bg-white">
          <table className="w-full min-w-[480px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-violet-100 bg-slate-50">
                <th className="px-2 py-1.5 font-semibold text-violet-700">
                  Evidences
                </th>
                <th className="px-2 py-1.5 font-semibold text-violet-700">
                  File Name / DOS
                </th>
                <th className="px-2 py-1.5 font-semibold text-violet-700">
                  Section
                </th>
                <th className="px-2 py-1.5 font-semibold text-violet-700">
                  Page
                </th>
              </tr>
            </thead>
            <tbody>
              {(driver.evidences ?? []).map((evidence, index) => (
                <tr
                  key={`${evidence.file}-${evidence.page}-${index}`}
                  className="border-b border-violet-50 last:border-0"
                >
                  <td className="px-2 py-1.5 text-gray-800">{evidence.text}</td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {evidence.file}
                    {evidence.date ? ` ${evidence.date}` : ""}
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {evidence.section || "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onEvidenceClick?.(evidence);
                      }}
                      className="font-bold text-violet-700 hover:underline"
                    >
                      {evidence.page}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasRationale && (
        <div className="space-y-2.5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-violet-700">
            Rationale
          </h3>

          {(driver.baselineRisk || driver.clinicalActivity) && (
            <div className="grid gap-2 sm:grid-cols-2">
              {driver.baselineRisk && (
                <div className="rounded-md border border-violet-200 bg-white px-2.5 py-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-violet-700">
                    Baseline Risk
                  </p>
                  <p
                    className={`mt-1 text-base font-bold ${baselineTone.text}`}
                  >
                    {driver.baselineRisk}
                  </p>
                </div>
              )}
              {driver.clinicalActivity && (
                <div className="rounded-md border border-violet-200 bg-white px-2.5 py-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-violet-700">
                    Clinical Activity
                  </p>
                  <p
                    className={`mt-1 text-base font-bold ${activityTone.text}`}
                  >
                    {driver.clinicalActivity}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTools.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-violet-700">
                Risk Models Applied
              </p>
              <div className="flex flex-wrap gap-1.5">
                {activeTools.map(({ label }) => (
                  <span
                    key={label}
                    className="rounded-full border border-violet-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-violet-700"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(hasReasons || hasMitigations) && (
            <div className="grid gap-2.5 sm:grid-cols-2">
              {hasReasons && (
                <div className="rounded-sm border border-red-400 border-l-4 bg-red-50/70 px-3 py-3">
                  <div className="mb-2 flex items-center gap-1.5">
                    <IconAlertCircle
                      size={16}
                      className="shrink-0 text-red-700"
                      aria-hidden
                    />
                    <p className="text-xs font-bold uppercase tracking-wide text-red-700">
                      Predicted Reasons
                    </p>
                    <AiMark />
                  </div>
                  <ul className="list-disc space-y-1.5 pl-4 text-sm text-gray-800 marker:text-red-700">
                    {(driver.predictedReadmissionReasons ?? []).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              {hasMitigations && (
                <div className="rounded-sm border border-emerald-500 border-l-4 bg-emerald-50/70 px-3 py-3">
                  <div className="mb-2 flex items-center gap-1.5">
                    <IconCheck
                      size={16}
                      className="shrink-0 text-emerald-700"
                      aria-hidden
                    />
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
                      Recommended Mitigations
                    </p>
                    <AiMark />
                  </div>
                  <ul className="list-disc space-y-1.5 pl-4 text-sm text-gray-800 marker:text-emerald-700">
                    {(driver.recommendedMitigations ?? []).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {hasDisposition && (
            <div>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-violet-700">
                Readmission Risk by Disposition
              </p>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
                {DISPOSITION_LABELS.map(({ key, label }) => {
                  const value = driver.dispositionSensitivity?.[key];
                  if (!value) return null;
                  const tone = getSeverityClasses(value);
                  return (
                    <div
                      key={key}
                      className="overflow-hidden rounded-sm border border-gray-200"
                    >
                      <div className="bg-slate-100 px-2 py-1.5 text-center">
                        <p className="text-sm font-medium text-gray-600">
                          {label}
                        </p>
                      </div>
                      <div className={`px-2 py-1.5 text-center ${tone.bg}`}>
                        <p className={`text-sm font-bold ${tone.text}`}>
                          {value}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ReadmissionDetailsPanel: React.FC<ReadmissionDetailsPanelProps> = ({
  drivers,
  onEvidenceClick,
  expandedKeys,
  onToggleRow,
  getRowKey,
}) => {
  const sortedDrivers = useMemo(
    () =>
      [...drivers].sort(
        (a, b) => b.readmissionDriverScore - a.readmissionDriverScore
      ),
    [drivers]
  );

  if (sortedDrivers.length === 0) {
    return (
      <p className="px-1 py-4 text-sm text-gray-500">
        No diagnosis readmission drivers recorded for this review.
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-violet-200">
        <div
          className={`${ROW_GRID} sticky top-0 z-10 border-b border-violet-200 bg-violet-100 px-2 py-2 text-sm font-semibold uppercase text-gray-700`}
        >
          <span>#</span>
          <span>Diagnosis</span>
          <span />
          <span>Risk</span>
          <span>Contribution</span>
          <span />
        </div>

        {sortedDrivers.map((driver, index) => {
          const key = getRowKey(driver, index);
          const expanded = expandedKeys.has(key);
          const principal = isPrincipal(driver.classification);
          const cc = hasCc(driver.classification);
          const mcc = hasMcc(driver.classification);

          return (
            <div
              key={key}
              className={`border-b border-violet-200 last:border-b-0 ${
                expanded
                  ? "bg-violet-100"
                  : index % 2 === 0
                    ? "bg-gray-50 hover:bg-violet-50"
                    : "bg-white hover:bg-violet-50"
              }`}
            >
              <button
                type="button"
                onClick={() => onToggleRow(key)}
                className={`${ROW_GRID} w-full px-2 py-2 text-left`}
              >
                <span className="text-sm font-medium tabular-nums text-gray-700">
                  {index + 1}
                </span>

                <div className="min-w-0">
                  <p className="line-clamp-2 text-sm font-medium text-gray-900">
                    {driver.diagnosis}
                  </p>
                  {(cc || mcc) && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {cc && (
                        <span className="rounded px-1 py-px text-[10px] font-bold leading-tight bg-[#f3d540] text-black">
                          CC
                        </span>
                      )}
                      {mcc && (
                        <span className="rounded px-1 py-px text-[10px] font-bold leading-tight bg-[#F3AC3F] text-black">
                          MCC
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  {principal && (
                    <span className="rounded px-1.5 py-0.5 text-xs font-bold text-white bg-[#1d4ed8]">
                      PDx
                    </span>
                  )}
                </div>

                <div>
                  <span
                    className={`inline-block rounded-md px-2 py-0.5 text-xs font-bold uppercase ${getRiskBadgeClass(
                      driver.predictedReadmissionRisk
                    )}`}
                  >
                    {driver.predictedReadmissionRisk}
                  </span>
                </div>

                <span className="text-sm font-semibold tabular-nums text-violet-700">
                  {driver.estimatedContributionPercent}%
                </span>

                <span className="flex justify-center text-violet-600">
                  {expanded ? (
                    <IconChevronDown size={16} aria-hidden />
                  ) : (
                    <IconChevronRight size={16} aria-hidden />
                  )}
                </span>
              </button>

              {expanded && (
                <DriverExpandedDetails
                  driver={driver}
                  onEvidenceClick={onEvidenceClick}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReadmissionDetailsPanel;
